import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WhatsAppSendProvider } from "@/context/WhatsAppSendContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Products from "@/pages/Products";
import Invoices from "@/pages/Invoices";
import InvoiceForm from "@/pages/InvoiceForm";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Payments from "@/pages/Payments";
import SalesDocs from "@/pages/SalesDocs";
import SalesDocForm from "@/pages/SalesDocForm";
import SalesDocDetail from "@/pages/SalesDocDetail";
import Vendors from "@/pages/Vendors";
import Purchases from "@/pages/Purchases";
import PurchaseForm from "@/pages/PurchaseForm";
import PurchaseDetail from "@/pages/PurchaseDetail";
import GSTReports from "@/pages/GSTReports";
import Taxes from "@/pages/Taxes";
import Crm from "@/pages/Crm";
import CompanySelect from "@/pages/CompanySelect";
import Inventory from "@/pages/Inventory";
import Reports from "@/pages/Reports";
import ExpandedReport from "@/pages/ExpandedReport";
import Settings from "@/pages/Settings";
import PurchasePayments from "@/pages/PurchasePayments";
import CreditNotes from "@/pages/CreditNotes";
import CreditNoteForm from "@/pages/CreditNoteForm";
import Pos from "@/pages/Pos";
import EwayBills from "@/pages/EwayBills";
import Transport from "@/pages/Transport";
import Expenses from "@/pages/Expenses";
import Certificates from "@/pages/Certificates";
import Agreements from "@/pages/Agreements";
import Approvals from "@/pages/Approvals";
import Notifications from "@/pages/Notifications";
import PublicInvoice from "@/pages/PublicInvoice";
import PublicStatement from "@/pages/PublicStatement";
import Recruitment from "@/pages/Recruitment";
import PublicRecruitment from "@/pages/PublicRecruitment";
import LegalLetters from "@/pages/LegalLetters";
import Catalogue from "@/pages/Catalogue";
import CatalogueShared from "@/pages/CatalogueShared";
import StatusAvatar from "@/components/StatusAvatar";
import PrintFooterLogo from "@/components/PrintFooterLogo";

function Protected({ children, permission }) {
  const { user, ready, can } = useAuth();
  if (!ready) return <StatusAvatar label="Loading your workspace…" className="min-h-screen" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!localStorage.getItem("vg_company")) return <Navigate to="/select-company" replace />;
  if (permission && !can(permission)) return <div className="min-h-screen grid place-items-center text-slate-500">You do not have permission to access this page.</div>;
  return <Layout>{children}</Layout>;
}

function AuthOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <StatusAvatar label="Loading your workspace…" className="min-h-screen" />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <WhatsAppSendProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/p/invoice/:token" element={<PublicInvoice />} />
            <Route path="/p/statement/:token" element={<PublicStatement />} />
            <Route path="/p/recruitment/:token" element={<PublicRecruitment />} />
            <Route path="/catalogue/shared/:token" element={<CatalogueShared />} />
            <Route path="/select-company" element={<AuthOnly><CompanySelect /></AuthOnly>} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/parties/customers" element={<Protected><Customers /></Protected>} />
            <Route path="/parties/customers/:id" element={<Protected><CustomerDetail /></Protected>} />
            <Route path="/products" element={<Protected><Products /></Protected>} />
            <Route path="/catalogue" element={<Protected><Catalogue /></Protected>} />
            <Route path="/sales/invoices" element={<Protected><Invoices /></Protected>} />
            <Route path="/sales/invoices/new" element={<Protected><InvoiceForm /></Protected>} />
            <Route path="/sales/invoices/:id/edit" element={<Protected><InvoiceForm /></Protected>} />
            <Route path="/sales/invoices/:id" element={<Protected><InvoiceDetail /></Protected>} />
            <Route path="/payments" element={<Protected><Payments /></Protected>} />

            <Route path="/sales/quotations" element={<Protected><SalesDocs dtype="quotation" /></Protected>} />
            <Route path="/sales/quotations/new" element={<Protected><SalesDocForm dtype="quotation" /></Protected>} />
            <Route path="/sales/quotations/:id/edit" element={<Protected><SalesDocForm dtype="quotation" /></Protected>} />
            <Route path="/sales/quotations/:id" element={<Protected><SalesDocDetail dtype="quotation" /></Protected>} />
            <Route path="/sales/orders" element={<Protected><SalesDocs dtype="sales_order" /></Protected>} />
            <Route path="/sales/orders/new" element={<Protected><SalesDocForm dtype="sales_order" /></Protected>} />
            <Route path="/sales/orders/:id/edit" element={<Protected><SalesDocForm dtype="sales_order" /></Protected>} />
            <Route path="/sales/orders/:id" element={<Protected><SalesDocDetail dtype="sales_order" /></Protected>} />
            <Route path="/sales/delivery-challans" element={<Protected><SalesDocs dtype="delivery_challan" /></Protected>} />
            <Route path="/sales/delivery-challans/new" element={<Protected><SalesDocForm dtype="delivery_challan" /></Protected>} />
            <Route path="/sales/delivery-challans/:id/edit" element={<Protected><SalesDocForm dtype="delivery_challan" /></Protected>} />
            <Route path="/sales/delivery-challans/:id" element={<Protected><SalesDocDetail dtype="delivery_challan" /></Protected>} />

            <Route path="/purchases/vendors" element={<Protected><Vendors /></Protected>} />
            <Route path="/purchases/invoices" element={<Protected><Purchases dtype="purchase_invoice" /></Protected>} />
            <Route path="/purchases/invoices/new" element={<Protected><PurchaseForm dtype="purchase_invoice" /></Protected>} />
            <Route path="/purchases/invoices/:id" element={<Protected><PurchaseDetail dtype="purchase_invoice" /></Protected>} />
            <Route path="/purchases/orders" element={<Protected><Purchases dtype="purchase_order" /></Protected>} />
            <Route path="/purchases/orders/new" element={<Protected><PurchaseForm dtype="purchase_order" /></Protected>} />
            <Route path="/purchases/orders/:id" element={<Protected><PurchaseDetail dtype="purchase_order" /></Protected>} />
            <Route path="/purchases/returns" element={<Protected><Purchases dtype="purchase_return" /></Protected>} />
            <Route path="/purchases/returns/new" element={<Protected><PurchaseForm dtype="purchase_return" /></Protected>} />
            <Route path="/purchases/returns/:id" element={<Protected><PurchaseDetail dtype="purchase_return" /></Protected>} />

            <Route path="/gst/reports" element={<Protected><GSTReports /></Protected>} />
            <Route path="/reports/taxes" element={<Protected><Taxes /></Protected>} />
            <Route path="/crm" element={<Protected><Crm /></Protected>} />
            <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/reports/expanded/:key" element={<Protected><ExpandedReport /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/purchases/payments" element={<Protected><PurchasePayments /></Protected>} />
            <Route path="/sales/credit-notes" element={<Protected><CreditNotes /></Protected>} />
            <Route path="/sales/credit-notes/new" element={<Protected><CreditNoteForm /></Protected>} />
            <Route path="/pos" element={<Protected><Pos /></Protected>} />
            <Route path="/gst/eway-bills" element={<Protected><EwayBills /></Protected>} />
            <Route path="/transport" element={<Protected><Transport /></Protected>} />
            <Route path="/expenses" element={<Protected><Expenses /></Protected>} />
            <Route path="/certificates" element={<Protected><Certificates /></Protected>} />
            <Route path="/agreements" element={<Protected><Agreements /></Protected>} />
            <Route path="/legal-letters" element={<Protected permission="manage_masters"><LegalLetters /></Protected>} />
            <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
            <Route path="/recruitment" element={<Protected permission="manage_users"><Recruitment /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </WhatsAppSendProvider>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <PrintFooterLogo />
      </AuthProvider>
    </div>
  );
}

export default App;
